

import { startTransition, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "@/hooks/useSearchParams";
import {
  validateEmail,
  validatePasswordBasic,
} from "@/lib/utils/input-validator";
import { useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { login } from "@/lib/api/handlers/AuthenticationHandling";
import { useAuth } from "@/lib/auth/AuthContext";
import { hydrateAccountPresentation } from "@/lib/auth/hydrateAccountPresentation";
import { isSwitchAccountIntent, safeNext, withNext } from "@/lib/auth/safeNext";
import { useRateLimitCountdown } from "@/hooks/useRateLimitCountdown";

/**
 * Initial state for the login form
 * Contains empty values for all form fields
 */
const initialState = {
  email: "",
  password: "",
};

/**
 * Custom hook for managing login form state and behavior
 * Handles form state, validation, error handling, and submission
 * @returns Form state and handler functions for the login form
 */
export function useLoginForm() {
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const { markAuthenticated, markMfaRequired } = useAuth();
  // State for form field values
  const [formData, setFormData] = useState(initialState);
  // State for validation errors by field
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  // Loading state during form submission
  const [isLoading, setIsLoading] = useState(false);
  // Rate-limit countdown state
  const rateLimit = useRateLimitCountdown();
  // Get translation function for error messages
  const t = useTranslations("errors");

  // Keep the countdown banner text in sync with the ticking clock
  useEffect(() => {
    if (!rateLimit.isLimited) return;
    setErrors((prev) => ({
      ...prev,
      password: t("rateLimitedCountdown", { seconds: rateLimit.secondsLeft }),
    }));
  }, [rateLimit.secondsLeft, rateLimit.isLimited, t]);

  /**
   * Updates form state when input values change
   * Also clears existing errors for the changed field if it passes validation
   * @param field - The form field being updated (email or password)
   * @param value - The new value for the field
   */
  function handleInputChange(field: "email" | "password", value: string) {
    // Update the form data with the new value
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    // If the field previously had an error, validate it again
    if (errors[field]) {
      let errorMessage = "";
      if (field === "email") {
        errorMessage = validateEmail(value, t);
      } else if (field === "password") {
        errorMessage = validatePasswordBasic(value, t);
      }

      // Clear the error if the field is now valid
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: errorMessage || undefined,
      }));
    }
  }

  /**
   * Validates all form fields and returns any validation errors
   * @returns Object containing validation errors for each field
   */
  function validateForm() {
    const validationErrors: { email?: string; password?: string } = {};

    // Validate email
    const emailError = validateEmail(formData.email, t);
    if (emailError) {
      validationErrors.email = emailError;
    }

    // Validate password
    const passwordError = validatePasswordBasic(formData.password, t);
    if (passwordError) {
      validationErrors.password = passwordError;
    }

    return validationErrors;
  }

  /**
   * Handles form submission, including validation and API calls
   * On successful login, redirects to the validated `?next=` deep link, else
   * the home page
   * On failure, displays appropriate error messages
   * @param e - Form submission event
   */
  async function handleSubmit(e?: React.FormEvent) {
    // Prevent default form submission
    if (e) e.preventDefault();

    // Block submission while under rate-limit countdown — the UI still shows
    // the remaining seconds via the live-updating errors.password banner.
    if (rateLimit.isLimited) return;

    // Validate all form fields
    const validationErrors = validateForm();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Clear previous errors and set loading state
    setErrors({});
    setIsLoading(true);

    try {
      // Attempt to log in with provided credentials
      const response = await login(formData.email, formData.password);

      if (response && "isServerError" in response && response.isServerError) {
        if (response.message === "rateLimited" && response.retryAfter) {
          rateLimit.start(response.retryAfter);
          setErrors({
            password: t("rateLimitedCountdown", {
              seconds: response.retryAfter,
            }),
          });
        } else {
          setErrors({ password: translateBackendError(t, response) });
        }
        setIsLoading(false);
        return;
      }

      // The login deep link (billing-comms Teil 2a): PrivateRoute wrote it,
      // `safeNext` decides whether it may be followed. Never the raw value.
      const next = safeNext(searchParams.get("next"));
      // "Sign in with another account" (billing mail link for another company):
      // the switch is the point, so the account-switch rule below is lifted —
      // for a `next` that `safeNext` accepted, and only with the exact marker.
      const switching = isSwitchAccountIntent(searchParams);

      // Check if MFA is required
      if (response && "mfaRequired" in response && response.mfaRequired) {
        // Backend `mfaRequired` only tells us yes/no, not delivery type
        // — the MFA-verify form lets the user pick (totp / email /
        // recovery). Default to totp; the auth-state machine only uses
        // `type` for the PrivateRoute mfa-required guard.
        markMfaRequired("totp", response.mfaExpiresAt);
        window.dispatchEvent(new Event("navigationStart"));
        // `next` rides along by URL; the MFA page validates it again.
        navigate(
          withNext(response.mfaSetupRequired ? "/auth/mfa-setup" : "/auth/mfa-verify", next, {
            switchAccount: switching,
          }),
        );
        return;
      }

      let landing = next ?? "/";
      let authenticate: (() => void) | null = null;
      // On success, flip AuthContext state THEN navigate. Routing
      // through useAuth ensures tokenStore + state stay in lockstep.
      if (response && "companyId" in response) {
        // Settle theme + language (and sweep the previous account's browser
        // state, if this is a different account) BEFORE flipping the auth
        // state: `/auth/login` sits under PublicOnlyRoute, which navigates
        // away the instant the state says authenticated — so anything awaited
        // after `markAuthenticated` happens with the dashboard already on
        // screen, which is the flash we are removing. Fail-open, time-capped.
        const hydrated = await hydrateAccountPresentation({ userId: response.userId });
        // A different account signing in on this browser: the deep link was
        // written for the previous one — not this one's to follow. Unless the
        // visitor asked for exactly that switch (`intent=switch`).
        if (hydrated?.accountSwitched === true && !switching) landing = "/";
        const { companyId, userId } = response;
        authenticate = () => markAuthenticated(companyId, userId);
      }
      window.dispatchEvent(new Event("navigationStart"));
      // ONE transition for the auth flip and the navigation. React Router
      // applies `navigate` as a transition; a bare `markAuthenticated` is an
      // urgent update, so PublicOnlyRoute used to render first on the OLD
      // location and its <Navigate> acted last — whatever the form chose, the
      // guard's target won (the race behind e2e LDR-2). In one transition both
      // updates commit in the same render: the login route is already gone.
      startTransition(() => {
        authenticate?.();
        navigate(landing);
      });
    } catch (error) {
      let errorArg: string | Error = "unknown";
      const axiosErr = error as { response?: { data?: { errorCode?: unknown } } };
      const code = axiosErr?.response?.data?.errorCode;
      if (typeof code === "string" && code.length > 0) errorArg = code;
      else if (error instanceof Error) errorArg = error;

      setErrors({ password: translateBackendError(t, errorArg) });
      setIsLoading(false);
    }
  }

  function prefetchDashboard() {
    
  }

  // Return the form state and handlers
  return {
    formData,
    errors,
    isLoading,
    handleInputChange,
    handleSubmit,
    prefetchDashboard,
    rateLimitSeconds: rateLimit.secondsLeft,
    rateLimited: rateLimit.isLimited,
  };
}
